import { z } from 'zod';
import { assetKindSchema } from './enums.js';
import { assetCommentSchema, ratingSchema } from './comments.js';
/**
 * Contrats de la VISIONNEUSE & des NOTES (T3, XPHOTO-21/-10). Source unique
 * front + back.
 *
 * - `noteSchema`         : une note textuelle horodatée (fil partagé sur la photo).
 * - `assetDetailSchema`  : détail COMPLET d'un asset pour la visionneuse (EXIF
 *   brut, empreintes, tags validés, notes, commentaires typés, note/vues, état,
 *   divergence disque↔DB).
 * - `createNoteSchema`   : corps de `POST /api/assets/:id/notes`.
 * - `updateNoteSchema`   : corps de `PATCH /api/assets/:id/notes/:noteId`.
 *
 * Le détail réutilise la forme d'`assetSchema` mais ajoute ce que la grille ne
 * porte pas (EXIF, hashes, tags, notes) : la grille reste légère, la visionneuse
 * charge le détail à la demande.
 */
/** Longueur maximale d'une note (garde-fou côté contrat, aligné front + back). */
export const NOTE_BODY_MAX = 4000;
/** Une note du fil partagé sur une photo (XPHOTO-10). */
export const noteSchema = z.object({
    id: z.uuid(),
    /** Nom affiché de l'auteur (dérivé de `users.display_name`). */
    authorName: z.string(),
    body: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
/**
 * Détail complet d'un asset pour la visionneuse (XPHOTO-21). Superset du DTO de
 * grille : ajoute EXIF brut, empreintes, tags validés et le fil de notes.
 */
export const assetDetailSchema = z.object({
    id: z.uuid(),
    libraryId: z.uuid(),
    relPath: z.string(),
    filename: z.string(),
    kind: assetKindSchema,
    mime: z.string(),
    width: z.number().int().nonnegative().nullable(),
    height: z.number().int().nonnegative().nullable(),
    sizeBytes: z.number().int().nonnegative(),
    durationS: z.number().nonnegative().nullable(),
    identityHash: z.string(),
    fileHash: z.string(),
    phash: z.string().nullable(),
    takenAt: z.string().datetime({ offset: true }).nullable(),
    /** Snapshot ExifTool brut (« vérité disque »), tel quel — peut être `null`. */
    exifSnapshot: z.record(z.string(), z.unknown()).nullable(),
    /** Dernière fois où le scanner a vu le fichier sur le disque. */
    seenAt: z.string().datetime({ offset: true }).nullable(),
    /** Fichier absent du disque au dernier scan (divergence disque↔DB). */
    isMissing: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    /** Note 0..5 (0 = non noté) — LOT 1 « données & tri ». */
    rating: ratingSchema,
    /** Compteur de vues GLOBAL de la photo (≥ 0) — LOT 1 « données & tri ». */
    viewCount: z.number().int().nonnegative(),
    /** Étiquettes des tags VALIDÉS (anglais, ordonnées) — vide avant T5. */
    tags: z.array(z.string()),
    /** Fil de notes, du plus ancien au plus récent. */
    notes: z.array(noteSchema),
    /** Commentaires TYPÉS, du plus ancien au plus récent (distincts des notes). */
    comments: z.array(assetCommentSchema),
});
/** Corps de création d'une note : texte non vide, borné. */
export const createNoteSchema = z.object({
    body: z.string().trim().min(1).max(NOTE_BODY_MAX),
});
/** Corps de mise à jour d'une note (même contrainte que la création). */
export const updateNoteSchema = createNoteSchema;
//# sourceMappingURL=asset-detail.js.map